import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  LoginRequest, LoginResponse, Runner, User, Account, CreateRunnerRequest, UpdateRunnerRequest, RunnerStateResponse,
  ExchangeCredential, CreateExchangeCredentialRequest, UpdateExchangeCredentialRequest,
  UserSettings, UpdateDefaultSettingsRequest, UpdateRiskSettingsRequest, UpdateNotificationSettingsRequest,
  Trade, CreateUserRequest, UpdateUserRequest, ResetPasswordRequest, ChangePasswordRequest,
  EmailServiceConfig, EmailServiceType, CreateEmailServiceConfigRequest, UpdateEmailServiceConfigRequest, TestEmailResult,
  RunnerLog, GridLevelEdit
} from '../types';

// Type-safe error message extraction
export function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    return (error.response?.data as { message?: string })?.message || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
}

class ApiService {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: '',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load token from localStorage on init
    this.token = localStorage.getItem('token');
    if (this.token) {
      this.setAuthHeader(this.token);
    }

    // Add response interceptor to handle 401 errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          this.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private setAuthHeader(token: string) {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/Auth/login', credentials);
    this.token = response.data.token;
    localStorage.setItem('token', this.token);
    this.setAuthHeader(this.token);
    return response.data;
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem('token');
    delete this.client.defaults.headers.common['Authorization'];
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/Auth/me');
    return response.data;
  }

  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await this.client.post('/Auth/change-password', data);
  }

  async getRunners(): Promise<Runner[]> {
    const response = await this.client.get<Runner[]>('/Runner/Runners');
    return response.data;
  }

  async getRunner(runnerId: number): Promise<Runner> {
    const response = await this.client.get<Runner>(`/Runner/Runner?runnerId=${runnerId}`);
    return response.data;
  }

  async startRunner(runnerId: number): Promise<Runner> {
    const response = await this.client.get<Runner>(`/Runner/StartRunner?runnerId=${runnerId}`);
    return response.data;
  }

  async stopRunner(runnerId: number): Promise<Runner> {
    const response = await this.client.get<Runner>(`/Runner/StopRunner?runnerId=${runnerId}`);
    return response.data;
  }

  async createRunner(data: CreateRunnerRequest): Promise<Runner> {
    const response = await this.client.post<Runner>('/Runner', data);
    return response.data;
  }

  async updateRunner(runnerId: number, data: UpdateRunnerRequest): Promise<Runner> {
    const response = await this.client.put<Runner>(`/Runner/${runnerId}`, data);
    return response.data;
  }

  async deleteRunner(runnerId: number): Promise<void> {
    await this.client.delete(`/Runner/${runnerId}`);
  }

  async getAccounts(): Promise<Account[]> {
    const response = await this.client.get<Account[]>('/Exchange/Accounts');
    return response.data;
  }

  async getRunnerState(runnerId: number): Promise<RunnerStateResponse | null> {
    try {
      const response = await this.client.get<RunnerStateResponse>(`/Runner/${runnerId}/State`);
      return response.data;
    } catch (error) {
      // Return null if state not available (runner never started, etc.)
      return null;
    }
  }

  async getRunnerTrades(runnerId: number, limit?: number): Promise<Trade[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await this.client.get<Trade[]>(`/Runner/${runnerId}/Trades${params}`);
    return response.data;
  }

  async getRunnerLogs(runnerId: number, limit?: number, since?: string): Promise<RunnerLog[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (since) params.append('since', since);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.client.get<RunnerLog[]>(`/Runner/${runnerId}/Logs${queryString}`);
    return response.data;
  }

  async getTrade(tradeId: number): Promise<Trade> {
    const response = await this.client.get<Trade>(`/Runner/Trades/${tradeId}`);
    return response.data;
  }

  // Exchange Credentials
  async getExchangeCredentials(): Promise<ExchangeCredential[]> {
    const response = await this.client.get<ExchangeCredential[]>('/ExchangeConfig');
    return response.data;
  }

  async getExchangeCredential(id: number): Promise<ExchangeCredential> {
    const response = await this.client.get<ExchangeCredential>(`/ExchangeConfig/${id}`);
    return response.data;
  }

  async createExchangeCredential(data: CreateExchangeCredentialRequest): Promise<ExchangeCredential> {
    const response = await this.client.post<ExchangeCredential>('/ExchangeConfig', data);
    return response.data;
  }

  async updateExchangeCredential(id: number, data: UpdateExchangeCredentialRequest): Promise<ExchangeCredential> {
    const response = await this.client.put<ExchangeCredential>(`/ExchangeConfig/${id}`, data);
    return response.data;
  }

  async activateExchangeCredential(id: number): Promise<void> {
    await this.client.put(`/ExchangeConfig/${id}/activate`);
  }

  async deactivateExchangeCredential(id: number): Promise<void> {
    await this.client.put(`/ExchangeConfig/${id}/deactivate`);
  }

  async deleteExchangeCredential(id: number): Promise<void> {
    await this.client.delete(`/ExchangeConfig/${id}`);
  }

  async reinitializeExchange(): Promise<void> {
    await this.client.post('/ExchangeConfig/reinitialize');
  }

  // User Settings
  async getSettings(): Promise<UserSettings> {
    const response = await this.client.get<UserSettings>('/Settings');
    return response.data;
  }

  async updateDefaultSettings(data: UpdateDefaultSettingsRequest): Promise<void> {
    await this.client.put('/Settings/defaults', data);
  }

  async updateRiskSettings(data: UpdateRiskSettingsRequest): Promise<void> {
    await this.client.put('/Settings/risk', data);
  }

  async updateNotificationSettings(data: UpdateNotificationSettingsRequest): Promise<void> {
    await this.client.put('/Settings/notifications', data);
  }

  // User Management
  async getUsers(): Promise<User[]> {
    const response = await this.client.get<User[]>('/User');
    return response.data;
  }

  async getUser(userId: number): Promise<User> {
    const response = await this.client.get<User>(`/User/${userId}`);
    return response.data;
  }

  async createUser(data: CreateUserRequest): Promise<User> {
    const response = await this.client.post<User>('/User', data);
    return response.data;
  }

  async updateUser(userId: number, data: UpdateUserRequest): Promise<User> {
    const response = await this.client.put<User>(`/User/${userId}`, data);
    return response.data;
  }

  async resetUserPassword(userId: number, data: ResetPasswordRequest): Promise<void> {
    await this.client.put(`/User/${userId}/password`, data);
  }

  async activateUser(userId: number): Promise<void> {
    await this.client.put(`/User/${userId}/activate`);
  }

  async deactivateUser(userId: number): Promise<void> {
    await this.client.put(`/User/${userId}/deactivate`);
  }

  async deleteUser(userId: number): Promise<void> {
    await this.client.delete(`/User/${userId}`);
  }

  // Email Service Configuration
  async getEmailServiceTypes(): Promise<EmailServiceType[]> {
    const response = await this.client.get<EmailServiceType[]>('/EmailService/types');
    return response.data;
  }

  async getEmailServiceConfigs(): Promise<EmailServiceConfig[]> {
    const response = await this.client.get<EmailServiceConfig[]>('/EmailService');
    return response.data;
  }

  async getEmailServiceConfig(id: number): Promise<EmailServiceConfig> {
    const response = await this.client.get<EmailServiceConfig>(`/EmailService/${id}`);
    return response.data;
  }

  async createEmailServiceConfig(data: CreateEmailServiceConfigRequest): Promise<EmailServiceConfig> {
    const response = await this.client.post<EmailServiceConfig>('/EmailService', data);
    return response.data;
  }

  async updateEmailServiceConfig(id: number, data: UpdateEmailServiceConfigRequest): Promise<EmailServiceConfig> {
    const response = await this.client.put<EmailServiceConfig>(`/EmailService/${id}`, data);
    return response.data;
  }

  async activateEmailServiceConfig(id: number): Promise<void> {
    await this.client.put(`/EmailService/${id}/activate`);
  }

  async deactivateEmailServiceConfig(id: number): Promise<void> {
    await this.client.put(`/EmailService/${id}/deactivate`);
  }

  async deleteEmailServiceConfig(id: number): Promise<void> {
    await this.client.delete(`/EmailService/${id}`);
  }

  async testEmailService(id: number, toEmail: string): Promise<TestEmailResult> {
    const response = await this.client.post<TestEmailResult>(`/EmailService/${id}/test`, { toEmail });
    return response.data;
  }

  // Grid Level Editing
  async updateGridLevels(runnerId: number, edits: GridLevelEdit[]): Promise<void> {
    await this.client.put(`/Runner/${runnerId}/GridLevels`, { edits });
  }
}

export const api = new ApiService();
